<template>
  <component :is="menuNavItemComponent" v-bind="componentProps" @click="trackNavigation" />
</template>

<script setup lang="ts">
import {
  HgMainNavItem,
  HgSideMenuNavItem,
  Is24MainNavItem,
  Is24SideMenuNavItem,
  MenuItem,
} from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { isPlatformIs24, isServer } from '@/helpers/Platform/Platform';
import { trackingFavouritesEvent } from '@/tracking/tracking';
import { LocalStorageKey } from '@/types';

type MenuNavItemType =
  | 'Is24MainNavItem'
  | 'Is24SideMenuNavItem'
  | 'HgMainNavItem'
  | 'HgSideMenuNavItem';

interface Props {
  menuNavItem?: MenuNavItemType;
}

const { menuNavItem = 'HgMainNavItem' } = defineProps<{
  menuNavItem?: MenuNavItemType;
}>();

const menuNavItemComponent = computed(() => {
  const componentMap: Record<MenuNavItemType, any> = {
    Is24MainNavItem,
    Is24SideMenuNavItem,
    HgMainNavItem,
    HgSideMenuNavItem,
  };

  return componentMap[menuNavItem];
});

const route = useRoute();
const { tm } = useI18n();

const isDotDisplayed = computed((): boolean => {
  const favouriteListings = !isServer() && localStorage.getItem(LocalStorageKey.FAVOURITE_LISTINGS);
  return !!favouriteListings && JSON.parse(favouriteListings).length > 0;
});

const icon = computed(() => {
  return isDotDisplayed.value ? 'HeartOutlineDot' : 'HeartOutline';
});

const componentProps = computed(() => {
  const menuItem: MenuItem = isPlatformIs24()
    ? tm('$is24.nav.favoriteMenuItem')
    : tm('$hg.nav.favoriteMenuItem');

  return {
    baseUrl: import.meta.env.VUE_APP_BASE_URL,
    icon: icon.value,
    menuItem,
    currentRoute: route,
  };
});

const trackNavigation = () => {
  const favouriteListings = localStorage.getItem(LocalStorageKey.FAVOURITE_LISTINGS);
  const favouritesCount = favouriteListings ? JSON.parse(favouriteListings).length : 0;
  trackingFavouritesEvent(favouritesCount);
};
</script>
